/*
 * Cart drawer behavior.
 *
 * Open triggers: the header cart icon and the custom events cart:open /
 * cart:refresh (dispatched by the PDP form intercept here and by the
 * bundle builder after a successful add). Contents re-render through
 * the Section Rendering API (GET /?sections=cart-drawer) so Liquid
 * stays the single source of truth for cart markup.
 *
 * Rows are grouped per variant (the BXGY bundle discount splits one
 * variant across cart lines): + adds one unit via /cart/add.js and lets
 * Shopify re-split, − trims the group's last underlying line by its key
 * via /cart/change.js, Remove zeroes every key in one /cart/update.js
 * call. The 5 lb upsell swatches post to /cart/add.js. The /cart page
 * keeps working as the no-JS fallback.
 */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  var busy = false;
  var opener = null;

  function updateHeaderBadge() {
    var inner = drawer.querySelector('[data-drawer-inner]');
    var count = inner ? inner.dataset.itemCount : null;
    if (count === null) return;
    var link = document.querySelector('.site-header__cart');
    if (!link) return;
    var badge = link.querySelector('.site-header__cart-count');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'site-header__cart-count';
      badge.setAttribute('aria-hidden', 'true');
      link.appendChild(badge);
    }
    badge.textContent = count;
  }

  var refreshSeq = 0;

  // Navidium injects its shipping-protection widget into the rendered
  // drawer, and a wholesale replaceWith would throw that work away — its
  // injection is click-triggered, so on reopen the app and our re-render
  // race, and whichever finishes last used to win. Adopting the app's
  // top-level clusters into the fresh render (moving nodes keeps their
  // listeners) makes the widget survive every re-render, so the offer
  // stays pinned to the bottom of the cart for as long as items exist.
  function adoptAppWidgets(current, next) {
    if (!current || !next) return;
    if (Number(next.dataset.itemCount) === 0) return;
    if (next.querySelector('[class*="nvd"], [id*="nvd"]')) return;
    var clusters = [];
    current.querySelectorAll('[class*="nvd"], [id*="nvd"]').forEach(function (n) {
      var p = n.parentElement;
      if (!p || !p.closest('[class*="nvd"], [id*="nvd"]')) clusters.push(n);
    });
    var newForm = next.querySelector('.drawer__foot form');
    if (!newForm) return;
    clusters.forEach(function (n) {
      var wasInForm = !!n.closest('form');
      if (wasInForm) newForm.appendChild(n);
      else newForm.parentElement.insertBefore(n, newForm);
    });
    // If the app hid the native checkout button (its protected-checkout
    // button stands in for it), carry that over too.
    var oldBtn = current.querySelector('.drawer__foot form [name="checkout"]');
    var newBtn = next.querySelector('.drawer__foot form [name="checkout"]');
    if (oldBtn && newBtn && oldBtn.style.display) newBtn.style.display = oldBtn.style.display;
  }

  function refresh() {
    // The cart drawer is a static section rendered identically on every
    // page, so render it from the canonical root path rather than the
    // current URL — some page paths don't resolve the Section Rendering
    // API for statically-included sections, which would leave the drawer
    // showing stale (often empty) content after an add.
    var seq = ++refreshSeq;
    return fetch('/?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        // Renders can race (header-icon open vs a just-fired add): only the
        // newest request may write, or a slow early response would install
        // pre-mutation markup — and with it stale data-keys.
        if (seq !== refreshSeq) return;
        var html = sections['cart-drawer'];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = doc.querySelector('[data-drawer-inner]');
        var current = drawer.querySelector('[data-drawer-inner]');
        if (next && current) {
          adoptAppWidgets(current, next);
          current.replaceWith(next);
          updateHeaderBadge();
        }
      })
      .catch(function () {});
  }

  function sayDrawer(msg) {
    var el = drawer.querySelector('[data-drawer-status]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = !msg;
  }

  // Shared by the three group controls. Never fails silently: HTTP errors
  // surface Shopify's own description (e.g. an inventory cap), and the
  // drawer re-renders even on failure — a 4xx usually means the DOM's
  // data-keys had gone stale, and only a fresh render can correct them.
  function mutate(url, payload) {
    busy = true;
    var lines = drawer.querySelector('.drawer__lines');
    if (lines) lines.classList.add('is-busy');
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        if (r.ok) return refresh();
        return r.json().catch(function () { return null; }).then(function (body) {
          return refresh().then(function () {
            sayDrawer((body && body.description) || 'Could not update your cart.');
          });
        });
      })
      .catch(function () {
        sayDrawer('Could not reach the store. Check your connection and try again.');
      })
      .finally(function () {
        busy = false;
        var l = drawer.querySelector('.drawer__lines');
        if (l) l.classList.remove('is-busy');
      });
  }

  function open() {
    opener = document.activeElement;
    drawer.hidden = false;
    // Next frame so the transition from translateX(100%) actually runs.
    requestAnimationFrame(function () {
      drawer.classList.add('is-open');
    });
    document.documentElement.classList.add('drawer-lock');
    var closeBtn = drawer.querySelector('.drawer__close');
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('drawer-lock');
    setTimeout(function () { drawer.hidden = true; }, 300);
    if (opener && opener.focus) opener.focus();
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('.site-header__cart');
    if (opener) {
      e.preventDefault();
      refresh().then(open);
      return;
    }
    if (!drawer.hidden && e.target.closest('[data-drawer-close]')) close();

    // data-keys carries the group's underlying cart lines as
    // "key=qty,key=qty". Keys are "<variantId>:<hash>", so split each
    // pair on its LAST '=' — never present in a key, but lastIndexOf
    // keeps this safe even if the format ever grows.
    function parseKeys(s) {
      return (s || '').split(',').filter(Boolean).map(function (pair) {
        var i = pair.lastIndexOf('=');
        return { key: pair.slice(0, i), qty: Number(pair.slice(i + 1)) };
      });
    }

    var plusBtn = e.target.closest('[data-group-plus]');
    if (plusBtn && !busy) {
      // add.js, not change.js: adding merges into whatever line structure
      // the automatic discount wants, so the split stays Shopify's problem.
      mutate('/cart/add.js', {
        items: [{ id: Number(plusBtn.dataset.variant), quantity: 1 }]
      });
    }

    var minusBtn = e.target.closest('[data-group-minus]');
    if (minusBtn && !busy) {
      var lines = parseKeys(minusBtn.dataset.keys);
      var last = lines[lines.length - 1];
      if (last) {
        mutate('/cart/change.js', {
          id: last.key,
          quantity: Math.max(0, last.qty - 1)
        });
      }
    }

    var removeBtn = e.target.closest('[data-group-remove]');
    if (removeBtn && !busy) {
      var updates = {};
      parseKeys(removeBtn.dataset.keys).forEach(function (l) { updates[l.key] = 0; });
      if (Object.keys(updates).length) {
        mutate('/cart/update.js', { updates: updates });
      }
    }

    // Upsell: bundle-builder-style pick, then confirm. Re-tapping the
    // picked swatch deselects. Preview swatches have no variant, so the
    // confirm button renders disabled and nothing can be added.
    var swatch = e.target.closest('[data-upsell-color]');
    if (swatch) {
      var upsellBox = swatch.closest('.drawer__upsell');
      var confirm = upsellBox.querySelector('[data-upsell-confirm]');
      var wasPicked = swatch.classList.contains('is-picked');
      upsellBox.querySelectorAll('[data-upsell-color]').forEach(function (b) {
        b.classList.remove('is-picked');
        b.setAttribute('aria-pressed', 'false');
      });
      var saveLine = upsellBox.querySelector('[data-upsell-savenote]');
      if (wasPicked) {
        confirm.hidden = true;
        if (saveLine) saveLine.hidden = true;
      } else {
        swatch.classList.add('is-picked');
        swatch.setAttribute('aria-pressed', 'true');
        confirm.hidden = false;
        confirm.textContent = 'Add ' + swatch.dataset.upsellLabel;
        confirm.disabled = !swatch.dataset.upsellVariant;
        confirm.dataset.variant = swatch.dataset.upsellVariant || '';
        if (saveLine) {
          saveLine.textContent = swatch.dataset.upsellSave || '';
          saveLine.hidden = !swatch.dataset.upsellSave;
        }
      }
    }

    var confirmBtn = e.target.closest('[data-upsell-confirm]');
    if (confirmBtn && !confirmBtn.disabled && confirmBtn.dataset.variant && !busy) {
      busy = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(confirmBtn.dataset.variant), quantity: 1 }] })
      })
        .then(refresh)
        .finally(function () { busy = false; });
    }
  });

  document.addEventListener('keydown', function (e) {
    if (drawer.hidden) return;
    if (e.key === 'Escape') close();
    // Keep Tab inside the open drawer.
    if (e.key === 'Tab') {
      var focusables = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  document.addEventListener('cart:refresh', refresh);
  document.addEventListener('cart:open', function () {
    refresh().then(open);
  });

  // Delegated, not bound per form: forms can arrive after load (the video
  // wall clones a buy panel out of a <template>), and a one-shot
  // querySelectorAll at init would never see them.
  // Without JS these submit natively to /cart/add, so nothing is lost.
  document.addEventListener('submit', function (e) {
    var pdpForm = e.target && e.target.closest ? e.target.closest('form[data-ajax-cart]') : null;
    if (!pdpForm) return;
    (function () {
      e.preventDefault();
      // getAttribute, NOT .id: a <form> containing <input name="id"> (which
      // every Shopify product form does - it carries the variant id) has a
      // named-property getter that shadows the built-in, so form.id returns
      // that INPUT element. Building a selector from it threw a SyntaxError
      // right after preventDefault(), which is why the button did nothing.
      var formId = pdpForm.getAttribute('id');
      var submitBtns = formId
        ? document.querySelectorAll(
            '#' + formId + ' [type="submit"], [form="' + formId + '"]'
          )
        : pdpForm.querySelectorAll('[type="submit"]');
      submitBtns.forEach(function (b) { b.disabled = true; });

      // Feedback target, if the section provides one. A silent failure is
      // indistinguishable from a dead button, so never fail quietly.
      var status = pdpForm.parentNode
        ? pdpForm.parentNode.querySelector('[data-cart-status]')
        : null;
      if (!status) status = document.querySelector('[data-cart-status]');
      function say(msg) {
        if (!status) return;
        status.textContent = msg;
        status.hidden = !msg;
      }
      say('');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(pdpForm)
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().catch(function () { return null; }).then(function (body) {
              var err = new Error((body && body.description) || 'add failed');
              // The server understood us and said no (sold out, needs a
              // selling plan, bad quantity). Resubmitting the same form
              // natively can only fail the same way — remember that.
              err.rejected = r.status >= 400 && r.status < 500;
              throw err;
            });
          }
          return refresh();
        })
        .then(function () {
          open();
          say('Added to your cart.');
        })
        .catch(function (err) {
          if (err && err.rejected) {
            // Keep the shopper here with the reason on screen instead of
            // bouncing them to /cart, where the native flow fails silently
            // with the same rejection.
            say(err.message || 'Could not add that to your cart.');
            return;
          }
          // Network-ish failure: hand off to the native cart flow so the
          // shopper still gets somewhere rather than nothing at all.
          say((err && err.message) || 'Could not add that. Taking you to your cart.');
          setTimeout(function () { pdpForm.submit(); }, 900);
        })
        .finally(function () {
          submitBtns.forEach(function (b) { b.disabled = false; });
        });
    })();
  });
})();
